import { Tabs, Tab as MUITab, styled, IconButton, Stack } from '@mui/material';
import { useState, useMemo } from 'react';

import PlusIcon from 'modules/common/components/Icons/PlusIcon';
import { useNavigationUpdate } from 'modules/common/components/Navigator/context';
import { MessageCenterScreen } from 'modules/common/components/Navigator/context/NavigatorContext.interface';
import { useTabPanelContext } from 'modules/IMC/MessageCenter/components/TabPanel/context';
import { useIMCContext } from 'modules/IMC/common/context';
import useMessageCenterCopy from 'modules/IMC/common/hooks/useMessageCenterCopy';
import NewMessageOptionsPopover, { PopoverOption } from 'modules/IMC/MessageCenter/components/NewMessageOptionsPopover';
import HelpAgentIcon from 'modules/common/components/Icons/HelpAgentIcon';
import MessageCenterIcon from 'modules/common/components/Icons/MessageCenterIcon';

const Tab = styled(MUITab)(() => ({
    minWidth: '100px',
    flexGrow: 'initial',
    paddingX: 1,
    letterSpacing: '0.4px',
}));

export default function MessageCenterTabs() {
    const { navigateToPage } = useNavigationUpdate();
    const { currentTab, setCurrentTab, tabNames } = useTabPanelContext();
    const { openHelpAgentWidget } = useIMCContext();
    const { helpAgentOptionLabel, newConversationOptionLabel } = useMessageCenterCopy();
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

    const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
        setCurrentTab(newValue);
    };

    const handlePlusButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        setPopoverAnchor(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setPopoverAnchor(null);
    };

    const popoverOptions = useMemo<PopoverOption[]>(
        () => [
            {
                label: helpAgentOptionLabel,
                icon: <HelpAgentIcon sx={{ fontSize: '24px' }} />,
                onClick: () => {
                    openHelpAgentWidget({ source: 'get_instant_answer_message_center_tab' });
                },
            },
            {
                label: newConversationOptionLabel,
                icon: <MessageCenterIcon sx={{ fontSize: '24px' }} />,
                onClick: () => navigateToPage(MessageCenterScreen.NEW_MESSAGE),
            },
        ],
        [openHelpAgentWidget, navigateToPage, helpAgentOptionLabel, newConversationOptionLabel]
    );

    return (
        <Stack direction="row" alignItems="center" gap={2.5} pt={2}>
            <Tabs
                value={currentTab}
                onChange={handleChange}
                textColor="primary"
                indicatorColor="primary"
                aria-label="message center tabs"
                sx={{ gap: 2.5 }}>
                {tabNames.map((tab) => (
                    <Tab
                        key={tab}
                        label={tab}
                        value={tab}
                        sx={{ textTransform: 'capitalize' }}
                        id={`message-center-tab-${tab}`}
                        aria-controls={`message-center-tabpanel-${tab}`}
                    />
                ))}
            </Tabs>
            <IconButton
                size="small"
                sx={{ marginLeft: 'auto' }}
                onClick={handlePlusButtonClick}
                color="primary"
                aria-label="Create new message">
                <PlusIcon />
            </IconButton>
            <NewMessageOptionsPopover
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                onClose={handlePopoverClose}
                options={popoverOptions}
            />
        </Stack>
    );
}
